import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api, setAuthToken, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { User, Mail, KeyRound, Save, AlertTriangle, Trash2, LogOut, ShieldCheck, ScrollText } from "lucide-react";
import { isNativeApp } from "@/lib/platform";

export default function EditProfile() {
    const { user, setUser, refresh, logout } = useAuth();
    const navigate = useNavigate();

    const [profile, setProfile] = useState({
        name: "",
        home_city: "",
        home_university: "",
        exchange_destination: "",
        bio: "",
        wants_exchange_buddy: true,
    });
    const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
    const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingEmail, setSavingEmail] = useState(false);
    const [savingPw, setSavingPw] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);

    const deleteAccount = async () => {
        setDeletingAccount(true);
        try {
            await api.delete("/auth/me");
            setAuthToken(null);
            setUser(false);
            toast.success("Your account and all data have been deleted");
            navigate("/");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't delete account");
            setDeletingAccount(false);
        }
    };

    useEffect(() => {
        if (user) {
            setProfile({
                name: user.name || "",
                home_city: user.home_city || "",
                home_university: user.home_university || "",
                exchange_destination: user.exchange_destination || "",
                bio: user.bio || "",
                wants_exchange_buddy: user.wants_exchange_buddy !== false,
            });
        }
    }, [user]);

    if (!user) return null;

    const saveProfile = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            await api.patch("/auth/me", profile);
            await refresh();
            toast.success("Profile saved");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save profile");
        } finally {
            setSavingProfile(false);
        }
    };

    const saveEmail = async (e) => {
        e.preventDefault();
        if (!emailForm.new_email || !emailForm.current_password) return;
        setSavingEmail(true);
        try {
            await api.post("/auth/change-email", emailForm);
            await refresh();
            toast.success("Email updated");
            setEmailForm({ new_email: "", current_password: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change email");
        } finally {
            setSavingEmail(false);
        }
    };

    const savePw = async (e) => {
        e.preventDefault();
        if (pwForm.new_password !== pwForm.confirm_password) {
            toast.error("New passwords do not match");
            return;
        }
        setSavingPw(true);
        try {
            await api.post("/auth/change-password", {
                current_password: pwForm.current_password,
                new_password: pwForm.new_password,
            });
            toast.success("Password changed");
            setPwForm({ current_password: "", new_password: "", confirm_password: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password");
        } finally {
            setSavingPw(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16 sm:py-10" data-testid="edit-profile-page">
            {/* Back link — web only. The native app uses the bottom tab bar for navigation. */}
            {!isNativeApp() && (
                <button
                    onClick={() => navigate("/dashboard")}
                    className="text-sm text-muted-foreground hover:text-foreground mb-4"
                    data-testid="edit-profile-back"
                >
                    ← Back to dashboard
                </button>
            )}
            {/* ── Hero card ─────────────────────────────────────────── */}
            <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-3xl p-6 sm:p-8 text-white shadow-xl"
                style={{ background: "linear-gradient(135deg, #f4a261 0%, #ee8f6d 45%, #dd6b4d 100%)" }}
            >
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-white/20 blur-2xl" aria-hidden />
                <div className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-white/10 blur-2xl" aria-hidden />
                <div className="relative flex items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-white/95 text-primary font-display text-2xl font-bold flex items-center justify-center shadow-lg shrink-0">
                        {(user.name || user.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/70">Profile</div>
                        <div className="font-display text-2xl sm:text-3xl font-bold truncate">{user.name || "Your profile"}</div>
                        <div className="text-white/80 text-sm truncate">{user.email}</div>
                    </div>
                </div>
                <div className="relative mt-5 flex flex-wrap gap-2">
                    <Button
                        data-testid="profile-logout-btn"
                        onClick={logout}
                        variant="outline"
                        className="rounded-full h-10 border-white/40 text-white hover:bg-white/10 hover:text-white bg-transparent"
                    >
                        <LogOut className="w-4 h-4 mr-1" /> Log out
                    </Button>
                    {user.is_admin && (
                        <Button
                            data-testid="profile-admin-btn"
                            onClick={() => navigate("/admin")}
                            variant="outline"
                            className="rounded-full h-10 border-white/40 text-white hover:bg-white/10 hover:text-white bg-transparent"
                        >
                            <ShieldCheck className="w-4 h-4 mr-1" /> Admin
                        </Button>
                    )}
                    <Button
                        data-testid="profile-terms-btn"
                        onClick={() => navigate("/terms")}
                        variant="outline"
                        className="rounded-full h-10 border-white/40 text-white hover:bg-white/10 hover:text-white bg-transparent"
                    >
                        <ScrollText className="w-4 h-4 mr-1" /> Terms of Use
                    </Button>
                </div>
            </motion.section>

            {/* Profile */}
            <SectionCard title="Personal details" icon={User} delay={0.05}>
                <form onSubmit={saveProfile} className="space-y-4" data-testid="edit-profile-form">
                    <div>
                        <Label htmlFor="name">Name</Label>
                        <Input id="name" data-testid="profile-name-input" className="mt-1 rounded-xl h-11"
                            value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="hcity">Home city</Label>
                            <Input id="hcity" data-testid="profile-home-city-input" className="mt-1 rounded-xl h-11"
                                placeholder="e.g. Amsterdam"
                                value={profile.home_city} onChange={(e) => setProfile({ ...profile, home_city: e.target.value })} />
                        </div>
                        <div>
                            <Label htmlFor="huni">University</Label>
                            <Input id="huni" data-testid="profile-home-university-input" className="mt-1 rounded-xl h-11"
                                value={profile.home_university} onChange={(e) => setProfile({ ...profile, home_university: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="dest">Exchange destination</Label>
                        <Input id="dest" data-testid="profile-exchange-destination-input" className="mt-1 rounded-xl h-11"
                            placeholder="e.g. Barcelona"
                            value={profile.exchange_destination} onChange={(e) => setProfile({ ...profile, exchange_destination: e.target.value })} />
                    </div>
                    <p className="text-xs text-muted-foreground -mt-2">
                        Please use the <strong>English / international</strong> name (e.g. Rome not Roma).
                    </p>
                    <div>
                        <Label htmlFor="bio">Bio</Label>
                        <Textarea id="bio" data-testid="profile-bio-input" className="mt-1 rounded-xl min-h-24"
                            value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} />
                    </div>
                    <div className="flex items-center justify-between rounded-2xl border border-border/60 p-3 bg-muted/30">
                        <div className="min-w-0 pr-3">
                            <div className="text-sm font-medium">Exchange Buddy matching</div>
                            <div className="text-xs text-muted-foreground">Match with people going the opposite direction for tips.</div>
                        </div>
                        <Switch
                            checked={profile.wants_exchange_buddy}
                            onCheckedChange={(v) => setProfile({ ...profile, wants_exchange_buddy: v })}
                            data-testid="profile-buddy-toggle"
                        />
                    </div>
                    <Button type="submit" disabled={savingProfile} data-testid="profile-save-btn"
                        className="rounded-full h-11 bg-primary hover:bg-primary/90">
                        <Save className="w-4 h-4 mr-1" />
                        {savingProfile ? "Saving…" : "Save changes"}
                    </Button>
                </form>
            </SectionCard>

            {/* Email */}
            <SectionCard title="Email" icon={Mail} delay={0.1}>
                <form onSubmit={saveEmail} className="space-y-4" data-testid="edit-email-form">
                    <div className="text-xs text-muted-foreground">Current: <span className="font-medium text-foreground">{user.email}</span></div>
                    <div>
                        <Label htmlFor="newEmail">New email</Label>
                        <Input id="newEmail" type="email" data-testid="email-new-input" className="mt-1 rounded-xl h-11"
                            value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} />
                    </div>
                    <div>
                        <Label htmlFor="curPwEmail">Current password</Label>
                        <Input id="curPwEmail" type="password" data-testid="email-current-password-input" className="mt-1 rounded-xl h-11"
                            value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} />
                    </div>
                    <Button type="submit" disabled={savingEmail} data-testid="email-save-btn"
                        className="rounded-full h-11 bg-primary hover:bg-primary/90">
                        {savingEmail ? "Updating…" : "Change email"}
                    </Button>
                </form>
            </SectionCard>

            {/* Password */}
            <SectionCard title="Password" icon={KeyRound} delay={0.15}>
                <form onSubmit={savePw} className="space-y-4" data-testid="edit-password-form">
                    <div>
                        <Label htmlFor="curPw">Current password</Label>
                        <Input id="curPw" type="password" data-testid="password-current-input" className="mt-1 rounded-xl h-11"
                            value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} />
                    </div>
                    <div>
                        <Label htmlFor="newPw">New password (min 6)</Label>
                        <Input id="newPw" type="password" data-testid="password-new-input" className="mt-1 rounded-xl h-11"
                            value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} />
                    </div>
                    <div>
                        <Label htmlFor="confirmPw">Confirm new password</Label>
                        <Input id="confirmPw" type="password" data-testid="password-confirm-input" className="mt-1 rounded-xl h-11"
                            value={pwForm.confirm_password} onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })} />
                    </div>
                    <Button type="submit" disabled={savingPw} data-testid="password-save-btn"
                        className="rounded-full h-11 bg-primary hover:bg-primary/90">
                        {savingPw ? "Updating…" : "Change password"}
                    </Button>
                </form>
            </SectionCard>

            {/* Danger zone — at the very bottom, as requested */}
            <motion.section
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="mt-8 border border-destructive/30 rounded-2xl p-5 sm:p-6 bg-destructive/5"
                data-testid="danger-zone"
            >
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-destructive/15 flex items-center justify-center shrink-0">
                        <AlertTriangle className="w-5 h-5 text-destructive" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-xl font-semibold">Danger zone</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Delete your account permanently. This removes your profile, all your rooms, all your messages and uploaded photos. This action cannot be undone.
                        </p>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button
                                    variant="outline"
                                    data-testid="delete-account-btn"
                                    className="mt-4 rounded-full border-destructive/60 text-destructive hover:bg-destructive hover:text-white"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete my account
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid="delete-account-dialog">
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Delete your account?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This will permanently delete your profile ({user.email}), all your room listings, all your conversations and any photos you uploaded. <strong>This cannot be undone.</strong>
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel data-testid="delete-account-cancel">Keep my account</AlertDialogCancel>
                                    <AlertDialogAction
                                        data-testid="delete-account-confirm"
                                        disabled={deletingAccount}
                                        onClick={deleteAccount}
                                        className="bg-destructive hover:bg-destructive/90"
                                    >
                                        {deletingAccount ? "Deleting…" : "Yes, delete everything"}
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                </div>
            </motion.section>
        </div>
    );
}

function SectionCard({ title, icon: Icon, delay = 0, children }) {
    return (
        <motion.section
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ delay, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 bg-white border border-border/60 rounded-3xl p-5 sm:p-6"
        >
            <div className="flex items-center gap-2 text-sm font-semibold mb-4">
                <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-primary" />
                </div>
                <span className="font-display text-base">{title}</span>
            </div>
            {children}
        </motion.section>
    );
}
